// Funding score inputs shared by the Funding Status card and the goal-planning save
// that runs when the user leaves the Risk Profile card.
import CONFIG from './config';
import { INVESTMENT_FIELDS } from '../components/wealth/investmentFields';

function presentValue(rate, nper, pmt, fv, type = 1) {
  if (rate === 0) return -((pmt * nper) + fv);
  return -(pmt * ((1 - Math.pow(1 + rate, -nper)) / rate) + fv / Math.pow(1 + rate, nper));
}

function numberValue(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

/** Sum of every investment card field (same fields the report counts as net worth). */
export function investmentsTotal(state) {
  return Object.values(INVESTMENT_FIELDS).reduce((sum, key) => sum + numberValue(state[key]), 0);
}

export function calculateFundingSnapshot(state, netWorth = 0) {
  const currentYear = new Date().getFullYear();
  const discountRate = CONFIG.EXPECTED_ROR[state.riskProfile] || CONFIG.EXPECTED_ROR.Balanced;

  const totalPVGoals = (state.goals || []).reduce((sum, goal) => {
    const targetYear = numberValue(goal.targetYear);
    const yearsToGoal = Math.max(0, targetYear - currentYear - 3);
    const shortTerm = Math.max(0, Math.min(3, targetYear - currentYear));
    const intermediatePV = presentValue(discountRate, yearsToGoal, 0, numberValue(goal.FV_goal), 0);
    const pv = presentValue(CONFIG.DEFAULT_DEBT_RETURN, shortTerm, 0, intermediatePV, 1);
    return sum + pv;
  }, 0);

  const totalPVEducation = (state.kidsEducation || []).reduce((sum, kid) => {
    const kidAge = numberValue(kid.Age);

    const yearsToUG = Math.max(0, CONFIG.DEFAULT_ug_AGE - kidAge - 3);
    const shortUG = Math.max(0, Math.min(3, CONFIG.DEFAULT_ug_AGE - kidAge));
    const interUGPV = presentValue(discountRate, yearsToUG, 0, numberValue(kid.ug_fees_FV), 0);
    const ugPV = presentValue(CONFIG.DEFAULT_DEBT_RETURN, shortUG, 0, interUGPV, 1);

    const yearsToPG = Math.max(0, CONFIG.DEFAULT_pg_AGE - kidAge - 3);
    const shortPG = Math.max(0, Math.min(3, CONFIG.DEFAULT_pg_AGE - kidAge));
    const interPGPV = presentValue(discountRate, yearsToPG, 0, numberValue(kid.pg_fees_FV), 0);
    const pgPV = presentValue(CONFIG.DEFAULT_DEBT_RETURN, shortPG, 0, interPGPV, 1);

    return sum + ugPV + pgPV;
  }, 0);

  const yearsForRetirement = Math.max(0, numberValue(state.yearsForRetirement));
  const amountRequiredToday =
    (numberValue(state.corpusRequired) / Math.pow(1 + discountRate, yearsForRetirement))
    + totalPVGoals
    + totalPVEducation
    + numberValue(state.emergencyFunds);

  const fundingScore = amountRequiredToday > 0
    ? Math.round((numberValue(netWorth) / amountRequiredToday) * 100)
    : 0;

  return {
    amountRequiredToday,
    fundingScore,
  };
}

/** goalsOnly: save goal-planning fields only (no Salesforce lead, net worth and score untouched). */
export function buildSubmitPayload(state, amountRequiredToday, fundingScore, netWorth, goalsOnly = false) {
  return {
    clientName:            state.clientName,
    emailId:               state.email,
    phoneNumber:           state.phoneNumber,
    birthYear:             state.birthYear,
    age:                   state.age,
    retirementAge:         state.retirementAge,
    yearlyExpenses:        state.monthlyExpensesInRetirement,
    corpusRequired:        state.corpusRequired,
    goals:                 state.goals || [],
    kidsEducation:         state.kidsEducation || [],
    riskProfile:           state.riskProfile,
    emergency_funds:       state.emergencyFunds,
    amountRequiredToday,
    currentInvestment:     netWorth,
    fundingScore,
    requiredRateOfReturn:  0,
    phoneNumberVerified:   true,
    city:                  state.city,
    receiveReport:         state.receiveReport,
    receiveGuide:          false,
    requestCallback:       state.requestCallback,
    goalsOnly,
  };
}
